import React, { useEffect, useState } from "react";
import Icon from "../base/Icon";
import { toastDefault, toastInfo } from "../base/Toast";
import { FontAwesomeIcons } from "../../core/Types";
import FlexRow from "../base/FlexRow";
import { useScreen } from "../../core/Global";
import { copyToClipboard } from "../../core/Helper";

interface TextBoxProps {
    id?: string
    label?: string
    labelUp?: boolean
    small?: boolean
    solid?: boolean
    clear?: boolean
    placeholder?: string
    className?: string
    name?: string
    parent?: string
    defaultValue?: string
    value?: any
    required?: boolean
    numeric?: boolean
    decimal?: boolean
    percent?: boolean
    autoFocus?: boolean
    maxLength?: number
    password?: boolean
    money?: boolean
    timeInput?: boolean
    generatePassword?: boolean
    email?: boolean
    ipAddress?: boolean
    inlineStyle?: boolean
    largeText?: boolean
    mediumText?: boolean
    marginBottom?: number
    noMarginBottom?: boolean
    readOnly?: boolean
    block?: boolean
    working?: boolean
    tooltip?: string
    startWith?: string
    noTextError?: boolean
    icon?: FontAwesomeIcons
    iconText?: string
    iconStart?: boolean
    iconType?: "solid" | "regular" | "light" | "thin" | "brands"
    iconColor?: string
    iconSize?: number
    iconClick?: () => void
    // A clickable button at the end of the input group (e.g. remove the field).
    actionIcon?: FontAwesomeIcons
    actionIconClass?: string
    actionTitle?: string
    actionClick?: () => void
    hint?: string
    view?: boolean
    disabled?: boolean
    tabIndex?: number
    onEditMode?: () => void
    onTextChanges?: (text: string) => void
    onChange?: React.ChangeEventHandler<HTMLInputElement> | undefined
}




export default function TextBox(props: TextBoxProps) {

    const moneyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        currencyDisplay: "code"
    });

    let element: HTMLInputElement | null;
    const id = "txt-" + (Math.floor(Math.random() * 1000000));
    const [inputId] = useState<string>(props.id ?? id);

    const screen = useScreen();

    const fontClass = () => {
        if (props.small) return "fs-8";
        return "fs-7";
    }

    const [text, textSet] = useState<string>("");
    const [focused, focusedSet] = useState<boolean>(false);
    const [viewMode, viewModeSet] = useState<boolean>(false);
    useEffect(() => {
        viewModeSet(props.view ?? false);
    }, [props.view])

    const [defaultValueLoaded, defaultValueLoadedSet] = useState<boolean>(false);

    const className = () => {
        var cls = "form-control form-inputs";
        if (props.small) cls += " form-control-sm";
        if (props.solid) cls += " form-control-solid";
        if (props.autoFocus) cls += " auto-focus";
        if (props.inlineStyle && !focused) cls += " textbox-inline-style";
        if (props.largeText) cls += " textbox-large-text";
        if (props.mediumText) cls += " textbox-medium-text";
        if (props.password) cls += " textbox-password";
        if (props.className) cls += " " + props.className;
        return cls;
    };

    const inputGroupClassName = () => {
        var cls = "input-group";
        if (props.small) cls += " input-group-sm";
        if (props.solid) cls += " input-group-solid";
        return cls;
    }

    const type = () => {
        var res = "text";
        //if (props.numeric || props.decimal) res = "tel";
        if (props.timeInput) res = "tel";
        else if (props.email) res = "email";
        else if (props.password) res = "password";
        //else if (props.money) res = "tel"
        return res;
    }

    var a = 1;
    const inputMode = () => {
        if (props.money || props.numeric || props.decimal || props.percent) return "decimal";
        return undefined;
    }

    // Percentages are always between 0 and 100, so 3 digits before the decimal point and 2 after it.
    const PERCENT_MAX_DIGITS = 3;
    const PERCENT_MAX_DECIMALS = 2;
    const PERCENT_MAX_LENGTH = PERCENT_MAX_DIGITS + 1 + PERCENT_MAX_DECIMALS;

    const formatPercent = (txt: string) => {
        txt = (txt ?? "").replace(/,/g, ".").replace(/[^0-9.]/g, "");

        // Only the first decimal point counts, the rest are dropped.
        const firstPoint = txt.indexOf(".");
        if (firstPoint > -1) {
            txt = txt.substring(0, firstPoint + 1) + txt.substring(firstPoint + 1).replace(/\./g, "");
        }

        var [integerPart, decimalPart] = txt.split(".");

        integerPart = integerPart.substring(0, PERCENT_MAX_DIGITS);
        while (integerPart.length > 1 && integerPart.startsWith("0")) integerPart = integerPart.substring(1);

        if (decimalPart === undefined) txt = integerPart;
        else txt = (integerPart === "" ? "0" : integerPart) + "." + decimalPart.substring(0, PERCENT_MAX_DECIMALS);

        if (txt != "" && Number.parseFloat(txt) > 100) txt = "100";
        return txt;
    }


    const keypress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (props.numeric) {
            if (!/[0-9]/.test(e.key) && e.key != "Enter") {
                e.preventDefault();
            }
        }

        if (props.decimal || props.money || props.percent) {
            if (!/[0-9]/.test(e.key) && e.key != "Enter" && e.key != "." && e.key != ",") {
                e.preventDefault();
            }
        }

        if (props.ipAddress && (text.endsWith(".") || text.split(".").length >= 4)) {
            if (!/[0-9]/.test(e.key) && e.key != "Enter") {
                e.preventDefault();
            }
        }

        if (props.ipAddress && !text.endsWith(".")) {
            if (!/[0-9]/.test(e.key) && e.key != "Enter" && e.key != ".") {
                e.preventDefault();
            }
        }

        if (props.timeInput) {

            if (text && text.length == 2) {
                if (!/[0-9]/.test(e.key) && e.key != "Enter" && e.key != ":") {
                    e.preventDefault();
                }
            }
            else {
                if (!/[0-9]/.test(e.key) && e.key != "Enter") {
                    e.preventDefault();
                }
            }




        }
    }

    const beforeInput = (e: any) => {
        if (props.money || props.decimal) {
            if (e.data === ",") {
                e.preventDefault();
                const cursor = e.target.selectionStart;
                const txt = text.slice(0, cursor) + "." + text.slice(cursor);
                textSet(formatMoney(txt))
            }
        }
    }

    const change = (e: React.ChangeEvent<HTMLInputElement>) => {

        window.hideFormError(props.name, props.parent);


        var txt = e.target.value ?? "";

        if (props.timeInput) {
            if (txt.length > 5) return;
            if (txt.length == 3) {
                var hh = Number.parseInt(txt[0] + txt[1]);
                if (hh > 23) hh = 23;
                if (txt[2] != ":") {
                    txt = (hh < 10 ? "0" : "") + hh + ":" + txt[2];
                }
            }

            if (txt.length == 5) {
                var hh = Number.parseInt(txt[0] + txt[1]);
                if (hh > 23) hh = 23;
                var mm = Number.parseInt(txt[3] + txt[4]);
                if (mm > 59) mm = 59;
                txt = (hh < 10 ? "0" : "") + hh + ":" + (mm < 10 ? "0" : "") + mm;
            }
        }

        if (props.ipAddress) {

            var ipArray = txt.split(".");
            var lastValue = ipArray[ipArray.length - 1];
            if (lastValue != "" && parseInt(lastValue) > 255) {
                ipArray[ipArray.length - 1] = '255';
            }
            txt = ipArray.join(".");
            if (lastValue.length == 3 && ipArray.length < 4) txt += ".";
        }

        if (props.money) {
            txt = formatMoney(txt);
        }

        if (props.percent) {
            txt = formatPercent(txt);
        }

        textSet(txt);
        if (props.onTextChanges) props.onTextChanges(txt);
        if (props.onChange) props.onChange(e);
    }

    const formatMoney = (txt: string) => {
        if (!txt) return "";
        const pointCount = (txt.match(/\./g) || []).length;
        if (pointCount > 1) {
            txt = txt.substring(0, txt.lastIndexOf("."));
        }

        const [integerPart, decimalPart] = txt.split(".");

        const formattedInteger = integerPart
            ? parseInt(integerPart.replace(/,/g, ""), 10).toLocaleString()
            : "";

        const formattedDecimal = decimalPart !== undefined
            ? `.${decimalPart.substring(0, 2)}`
            : "";
        txt = formattedInteger + formattedDecimal;
        return txt;
    }


    const focus = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        focusedSet(true);
    }
    const blur = (e: React.FocusEvent<HTMLInputElement, Element>) => {
        focusedSet(false);
    }

    const mouseOver = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        // if(props.password) {
        //     $(e.target).attr("type", "text");
        // }
    }

    const mouseOut = (e: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
        // if (props.password) {
        //     $(e.target).attr("type", "password");
        // }
    }

    useEffect(() => {
        window.makeFormTooltip((inputId) + "-tooltip");
        if (props.autoFocus) {
            setTimeout(() => {
                $("#" + props.id).focus();
            }, 200);
        }

    }, []);


    // Legacy values can be out of range, so bring them back into 0-100 when the field loads.
    const normalizePercent = (value: any) => {
        const num = Number.parseFloat((value ?? "").toString().replace(/,/g, "."));
        if (isNaN(num)) return "";
        const rounded = Math.round(num * Math.pow(10, PERCENT_MAX_DECIMALS)) / Math.pow(10, PERCENT_MAX_DECIMALS);
        return Math.min(100, Math.max(0, rounded)).toString();
    }

    useEffect(() => {
        if (props.defaultValue && !defaultValueLoaded) {
            textSet(props.percent ? normalizePercent(props.defaultValue) : props.defaultValue);
            defaultValueLoadedSet(true);
        }
        else {
            var t = props.value ?? "";
            if (props.money && props.value && t != "") {
                t = formatMoney(t.toString());
            }
            if (props.percent && t !== "") {
                t = normalizePercent(t);
            }
            textSet(t);
        }
    }, [props.defaultValue, props.value])


    useEffect(() => {
        if (props.clear) {
            textSet("");
        }
    }, [props.clear])




    return (
        <div className={"row align-items-center" + (props.noMarginBottom ? "" : " mb-" + (props.marginBottom ?? 5))}>
            {
                props.label &&
                <label className={`col col-12 ${(props.labelUp ? "mb-1" : (props.block ? "col-md-3" : "col-md-2"))} ${fontClass()}`} htmlFor={inputId}>
                    {props.label}
                    {
                        props.tooltip &&
                        <i className="fa-solid fa-circle-question ms-2 form-tooltips"
                            id={(inputId) + "-tooltip"}
                            data-bs-placement="right"
                            data-bs-custom-class="tooltip-dark"
                            title={props.tooltip}></i>
                    }
                    {
                        props.hint &&
                        <i className="fa-solid fa-circle-question ms-1" title={props.hint}></i>
                    }
                    {
                        props.labelUp && !props.noTextError && <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                    }
                </label>
            }
            <div className={"col col-12 " + (props.labelUp ? "" : (props.block && props.label ? "col-md-9" : props.block ? "col-md-12" : "col-md-6"))}>
                <div className={(props.password || props.icon || props.iconText || props.actionIcon) ? inputGroupClassName() : ""} style={{
                    position: "relative"
                }}>
                    {
                        viewMode ?
                            <>
                                <FlexRow className={className() + " form-control-solid"}>
                                    <div className="me-2">
                                        {
                                            props.password ? "*******" : text
                                        }
                                    </div>
                                    <a href="#" onClick={e => {
                                        e.preventDefault();
                                        viewModeSet(false);
                                        if (props.onEditMode) props.onEditMode();
                                    }}>
                                        <Icon icon="edit" />
                                    </a>
                                </FlexRow>
                            </>
                            :
                            <>
                                {
                                    props.icon && props.iconStart &&
                                    <span className="input-group-text">
                                        <Icon icon={props.icon} type={props.iconType}
                                            color={props.iconColor}
                                            size={props.iconSize} />
                                    </span>
                                }

                                <input id={inputId}
                                    ref={e => element = e}
                                    name={props.name}
                                    type={type()}
                                    inputMode={inputMode()}
                                    lang="en"
                                    autoComplete={props.password ? "new-password" : "one-time-code"}
                                    className={className()}
                                    onKeyPress={keypress}
                                    autoFocus={props.autoFocus}
                                    maxLength={props.percent ? PERCENT_MAX_LENGTH : props.maxLength}
                                    value={text}
                                    placeholder={props.timeInput ? "08:30" : props.placeholder}
                                    readOnly={props.readOnly}
                                    disabled={props.disabled}
                                    onChange={change}
                                    onBeforeInput={beforeInput}
                                    onFocus={focus}
                                    onBlur={blur}
                                    onMouseOver={mouseOver}
                                    onMouseOut={mouseOut}
                                    tabIndex={props.tabIndex}
                                />
                                {
                                    props.password && props.generatePassword &&
                                    <span className="fs-8 text-primary" style={{
                                        position: "absolute",
                                        right: 50,
                                        top: 14,
                                        cursor: "pointer",
                                        zIndex: 10
                                    }} onClick={e => {
                                        var p = window.getRandomString(8);
                                        textSet(p);
                                        copyToClipboard(p, "New password generated and has been copied to the clipboard!");
                                    }}>
                                        Generate
                                    </span>
                                }

                                {
                                    props.password &&
                                    <span className="input-group-text cursor-pointer"
                                        onMouseDown={e => $("#" + inputId).attr("type", "text")}
                                        onMouseUp={e => $("#" + inputId).attr("type", "password")}
                                    >
                                        <Icon icon="eye" />
                                    </span>
                                }
                                {
                                    props.icon && !props.iconStart &&
                                    <span className="input-group-text">
                                        <Icon icon={props.icon} type={props.iconType}
                                            color={props.iconColor}
                                            size={props.iconSize} />
                                    </span>
                                }
                                {
                                    props.actionIcon &&
                                    <span className="input-group-text cursor-pointer" title={props.actionTitle}
                                        onClick={props.actionClick}>
                                        <Icon icon={props.actionIcon} className={props.actionIconClass} />
                                    </span>
                                }

                                {
                                    props.iconText &&
                                    <span className={`input-group-text fs-8 ${props.iconClick ? "cursor-pointer" : ""}`}
                                        onClick={props.iconClick}>
                                        {props.iconText}
                                    </span>
                                }

                                {
                                    props.working &&
                                    <span style={{
                                        position: "absolute",
                                        right: 7,
                                        top: 7,
                                        zIndex: 10
                                    }}>
                                        <Icon icon="spinner-third" spin />
                                    </span>
                                }
                            </>
                    }

                </div>
            </div>
            {
                props.block && props.label && !props.labelUp &&
                <div className="col col-12 col-md-3"></div>
            }
            {
                !props.labelUp && !props.noTextError &&
                <div className={"col col-12 " + (props.block ? "col-md-9" : "col-md-4")}>
                    <span id={props.name + "-err"} className="small text-danger mx-3 form-error"></span>
                </div>
            }



        </div>
    )

}